import { useMemo, useState } from "react";
import { Sparkles, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePageTranslation } from "@/i18n/useTranslation";
import { useLanguage } from "@/i18n/useLanguage";
import { supportTranslations as enSupport } from "@/i18n/locales/en/support";
import { supportTranslations as thSupport } from "@/i18n/locales/th/support";

/**
 * Ticket deflection: as the customer types a subject, suggest self-serve
 * articles that might answer them first. Mock content for now — wire to a real
 * KB / AI search later. Keyword-matched, language-aware.
 */
interface KbArticle {
  id: string;
  keywords: string[];
  en: { title: string; snippet: string };
  th: { title: string; snippet: string };
}

const KB_ARTICLES: KbArticle[] = [
  {
    id: "orders-after-payment",
    keywords: ["order", "payment", "paid", "ออเดอร์", "คำสั่งซื้อ", "ชำระ", "จ่าย", "เงิน"],
    en: {
      title: "Orders not appearing after a customer pays",
      snippet:
        "This is usually a delayed payment webhook. Check Orders → filter by date; if it's still missing after 15 minutes, open a request and we'll reprocess it.",
    },
    th: {
      title: "ออเดอร์ไม่เข้าระบบหลังลูกค้าชำระเงิน",
      snippet:
        "ส่วนใหญ่เกิดจาก webhook การชำระเงินล่าช้า ลองดูที่หน้าออเดอร์แล้วกรองตามวันที่ หากเกิน 15 นาทีแล้วยังไม่ขึ้น เปิดคำขอไว้ เราจะดึงออเดอร์เข้าระบบให้",
    },
  },
  {
    id: "add-staff",
    keywords: ["staff", "team", "user", "permission", "access", "พนักงาน", "ทีม", "ผู้ใช้", "สิทธิ์"],
    en: {
      title: "Add a staff member and limit their access",
      snippet:
        "Go to Settings → Team → Invite member, then pick a role (e.g. Orders only). Admins see everything; members only see the apps you grant.",
    },
    th: {
      title: "เพิ่มพนักงานและจำกัดสิทธิ์การเข้าถึง",
      snippet:
        "ไปที่ ตั้งค่า → ทีมงาน → เชิญสมาชิก แล้วเลือกบทบาท (เช่น เห็นเฉพาะออเดอร์) ผู้ดูแลเห็นทุกอย่าง ส่วนสมาชิกเห็นเฉพาะแอปที่คุณให้สิทธิ์",
    },
  },
  {
    id: "tax-invoice",
    keywords: ["invoice", "tax", "receipt", "ใบกำกับ", "ภาษี", "ใบเสร็จ", "บิล"],
    en: {
      title: "Request a full tax invoice",
      snippet:
        "We can issue a full tax invoice in your company name. Open a Billing request with the company name, address and tax ID you'd like on it.",
    },
    th: {
      title: "ขอใบกำกับภาษีเต็มรูป",
      snippet:
        "เราออกใบกำกับภาษีเต็มรูปในนามบริษัทของคุณได้ เปิดคำขอหมวดการเงิน/ใบเสร็จ พร้อมแจ้งชื่อบริษัท ที่อยู่ และเลขผู้เสียภาษี",
    },
  },
  {
    id: "storefront-slow",
    keywords: ["slow", "speed", "loading", "lag", "ช้า", "โหลด", "หน่วง"],
    en: {
      title: "Storefront is loading slowly",
      snippet:
        "Most slowdowns come from large product images not served via CDN. Try again after a few minutes; if it persists, tell us the time of day it's slow.",
    },
    th: {
      title: "หน้าร้านโหลดช้า",
      snippet:
        "ส่วนใหญ่เกิดจากรูปสินค้าขนาดใหญ่ที่ไม่ได้เสิร์ฟผ่าน CDN ลองใหม่อีกครั้งในไม่กี่นาที หากยังช้าอยู่ แจ้งช่วงเวลาที่ช้าให้เราด้วย",
    },
  },
  {
    id: "coupons",
    keywords: ["coupon", "discount", "promo", "คูปอง", "ส่วนลด", "โปรโมชั่น", "โค้ด"],
    en: {
      title: "Set up a discount coupon",
      snippet:
        "Create coupons under Promotions. You can set a code, amount or percentage, usage limits and an expiry date.",
    },
    th: {
      title: "ตั้งค่าคูปองส่วนลด",
      snippet:
        "สร้างคูปองได้ที่เมนูโปรโมชั่น ตั้งรหัสคูปอง จำนวนเงินหรือเปอร์เซ็นต์ จำนวนครั้งที่ใช้ได้ และวันหมดอายุ",
    },
  },
];

function matchArticles(query: string, lang: "en" | "th"): KbArticle[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  return KB_ARTICLES.filter((a) => {
    const title = a[lang].title.toLowerCase();
    return a.keywords.some((k) => q.includes(k.toLowerCase())) || title.includes(q);
  }).slice(0, 3);
}

export function KnowledgeBaseSuggestions({ query }: { query: string }) {
  const { language } = useLanguage();
  const t = usePageTranslation({ en: enSupport, th: thSupport }) as typeof enSupport;
  const lang: "en" | "th" = language === "th" ? "th" : "en";
  const matches = useMemo(() => matchArticles(query, lang), [query, lang]);
  const [openId, setOpenId] = useState<string | null>(null);

  if (matches.length === 0) return null;

  return (
    <div className="mt-2 overflow-hidden rounded-xl border border-blue-100 bg-blue-50/50 dark:border-blue-500/20 dark:bg-blue-500/[0.06]">
      <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1 text-xs font-medium text-primary dark:text-primary">
        <Sparkles className="h-3.5 w-3.5" />
        {t.kbTitle}
      </div>
      <div className="p-1">
        {matches.map((a) => {
          const open = openId === a.id;
          return (
            <div key={a.id}>
              <button
                type="button"
                onClick={() => setOpenId(open ? null : a.id)}
                className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-white/70 dark:text-gray-200 dark:hover:bg-white/5"
              >
                <span className="min-w-0 truncate">{a[lang].title}</span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 shrink-0 text-gray-400 transition-transform",
                    open && "rotate-180",
                  )}
                />
              </button>
              {open && (
                <p className="px-2.5 pb-2.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                  {a[lang].snippet}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
